import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthActions } from '../../shared/store/auth/auth.actions';
import { AuthToken } from '../../domain/auth/model/auth-token';
import { AuthSelectors } from '../../shared/store/auth/auth.selectors';
import { AuthFacadeService } from '../../domain/auth/service/auth-facade.service';
import { ApplicationConfig } from '../config/application.config';

const AUTH_RETRY_HEADER = 'X-Auth-Retry';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private refreshingToken = false;

  private refreshTokenSubject = new BehaviorSubject<boolean | null>(null);

  private store: Store = inject(Store);
  private authFacade: AuthFacadeService = inject(AuthFacadeService);

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.headers.has(AUTH_RETRY_HEADER)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (
          error instanceof HttpErrorResponse &&
          [401].includes(error.status) &&
          this.isAuthenticationRequired(request.url)
        ) {
          if (this.isUserMeRequest(request.url)) {
            return throwError(() => error);
          }

          const token: AuthToken | null = this.store.selectSnapshot(
            AuthSelectors.token
          );
          if (!token?.accessToken) {
            return throwError(() => error);
          }

          if (this.refreshingToken) {
            return this.refreshTokenSubject.pipe(
              filter(result => result !== null),
              take(1),
              switchMap(result =>
                result
                  ? next.handle(
                      this.addAuthenticationToken(this.markRetry(request))
                    )
                  : throwError(() => new Error('Session expired'))
              )
            );
          } else {
            this.refreshingToken = true;
            this.refreshTokenSubject.next(null);

            return this.store.dispatch(new AuthActions.RefreshToken()).pipe(
              switchMap(() => {
                this.refreshingToken = false;
                this.refreshTokenSubject.next(true);

                return next.handle(
                  this.addAuthenticationToken(this.markRetry(request))
                );
              }),
              catchError(() => {
                this.refreshingToken = false;
                this.refreshTokenSubject.next(false);
                this.authFacade.sessionExpired();
                return throwError(() => new Error('Session expired'));
              })
            );
          }
        }

        return throwError(() => error);
      })
    );
  }

  private isAuthenticationRequired(url: string): boolean {
    return url.includes(ApplicationConfig.apiUrl);
  }

  private isUserMeRequest(url: string): boolean {
    return url.includes('/users/me');
  }

  private addAuthenticationToken(request: HttpRequest<unknown>) {
    const token: AuthToken | null = this.store.selectSnapshot(
      AuthSelectors.token
    );

    return !token
      ? request
      : request.clone({
          setHeaders: {
            Authorization: 'Bearer ' + token.accessToken,
          },
        });
  }

  private markRetry(request: HttpRequest<unknown>): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        [AUTH_RETRY_HEADER]: '1',
      },
    });
  }
}
